import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isNonBlockingStepForReleaseType, getReleaseTypeForNonBlockingSteps } from '../helpers';
import { RootState } from '../reducers/reducers';
import { ReleaseStepStatus, ReleaseStepType, ReleaseTimelinePhase } from '../types';
import {
    CheckCircleFilled,
    CloseCircleTwoTone,
    ClockCircleTwoTone,
    ClockCircleOutlined,
    DeleteRowOutlined,
} from '@ant-design/icons';
import { antGold, antGrey, antRed, colorWithAlpha, runwayColors, rwColors } from './styles/globalStyles';
import { useThemeMode } from './styles/themeMode';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import {
    selectRelease,
    selectReleaseStepsBlockingConfigForReleaseType,
    selectSubmittedRollback,
} from '../selectors/selectors';
import { displayTextForReleaseStepStatus } from '../helpers/displayHelpers';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../helpers/aclHelpers';
import Modal from 'antd/lib/modal';
import { DeleteReleaseRequest, TRY_DELETE_RELEASE_ENTITY, tryDeleteRelease } from '../actions/release';
import { ReleaseComponentProps } from '../typescriptHelpers';
import { globalMessage } from '../utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface StepStatusAlertProps {
    status: ReleaseStepStatus;
    statusReasonString: string;
    nonBlockingStep?: boolean;
    action?: React.ReactNode;
    style?: React.CSSProperties;
}

export const StepStatusAlert: React.FC<StepStatusAlertProps> = ({
    status,
    statusReasonString,
    nonBlockingStep = false,
    action,
    style,
}) => {
    const { mode } = useThemeMode();
    const isDarkMode = mode === 'dark';
    const backgroundColor =
        !isDarkMode ?
            nonBlockingStep ? antGrey.grey3
            :   backgroundColorForStatus(status)
        :   undefined;
    const borderColor =
        !isDarkMode ?
            nonBlockingStep ? antGrey.grey5
            :   outlineColorForStatus(status)
        :   undefined;
    const statusString = displayTextForReleaseStepStatus(status);
    const alertType =
        isDarkMode ?
            nonBlockingStep ? 'info'
            :   alertTypeForStatus(status)
        :   undefined;

    return (
        <Alert
            showIcon
            closable={false}
            icon={iconForStatus(status, nonBlockingStep, isDarkMode)}
            type={alertType}
            message={
                <Text>
                    <Text strong>
                        {statusString}
                        {statusReasonString ? ': ' : ''}
                    </Text>
                    {statusReasonString}
                </Text>
            }
            style={{
                backgroundColor,
                borderColor,
                display: 'inline-flex',
                ...style,
            }}
            action={action}
        />
    );
};

interface StepStatusHeaderComponentProps extends ReleaseComponentProps {
    orgIdParam: string;
    appIdParam: string;
    releaseTimelinePhase: ReleaseTimelinePhase;
    releaseStepType: ReleaseStepType;
    status: ReleaseStepStatus;
    statusReasonString: string;
}

const StepStatusHeaderComponent = (props: StepStatusHeaderComponentProps) => {
    const dispatch = useDispatch();
    const { status, statusReasonString } = props;

    const activeRollback = useSelector((state: RootState) => selectSubmittedRollback(state, props.appId));

    const discardRollbackIsLoading = useIsLoading(TRY_DELETE_RELEASE_ENTITY, activeRollback?.releaseId);
    const discardRollbackError = useError(TRY_DELETE_RELEASE_ENTITY, activeRollback?.releaseId);
    const discardRollbackSuccess = useUpdated(TRY_DELETE_RELEASE_ENTITY, activeRollback?.releaseId);

    const releaseType = useSelector((state: RootState) => {
        const release = selectRelease(state, props.releaseId);

        return release.type;
    });

    const nonBlockingStepsConfigMap = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, props.appId),
    );

    const nonBlockingStep = isNonBlockingStepForReleaseType(
        nonBlockingStepsConfigMap[getReleaseTypeForNonBlockingSteps(releaseType)],
        props.releaseStepType,
    );

    const needsDiscardCTA =
        [ReleaseStepType.Metadata, ReleaseStepType.Screenshots, ReleaseStepType.Submission].includes(
            props.releaseStepType,
        ) &&
        props.releaseTimelinePhase === ReleaseTimelinePhase.Current &&
        status === ReleaseStepStatus.Inactive &&
        !!activeRollback;

    const deleteRelease = () => {
        if (!activeRollback) {
            globalMessage.error('No active rollback exists.');
            return;
        }

        const request: DeleteReleaseRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: activeRollback.id,
            orgIdParam: props.orgIdParam,
            appIdParam: props.appIdParam,
        };

        dispatch(tryDeleteRelease(request));
    };

    const showConfirmDiscardRollback = () => {
        Modal.confirm({
            title: `Are you sure you want to discard the active rollback (${activeRollback!.version})?`,
            icon: null,
            content:
                'Discarding the active rollback will delete the record from Runway and developer reject the submitted rollback build.',
            okText: `Yes, discard release`,
            okButtonProps: {
                type: 'primary',
            },
            cancelText: 'Never mind',
            onOk: deleteRelease,
            maskClosable: true,
        });
    };

    React.useEffect(() => {
        if (discardRollbackError) {
            globalMessage.error(discardRollbackError);
        }
    }, [discardRollbackError]);

    React.useEffect(() => {
        if (discardRollbackSuccess) {
            globalMessage.success('Rollback discarded!');
        }
    }, [discardRollbackSuccess]);

    const discardAction =
        needsDiscardCTA ?
            <PermissionedActionButton
                size="small"
                actionType={RunwayUserAction.UACreateSkipDeleteCompleteReleases}
                icon={<DeleteRowOutlined style={{ color: runwayColors.primary }} />}
                onClick={showConfirmDiscardRollback}
                loading={discardRollbackIsLoading}
            >
                Discard {activeRollback.version} rollback
            </PermissionedActionButton>
        :   null;

    return (
        <StepStatusAlert
            status={status}
            statusReasonString={statusReasonString}
            nonBlockingStep={nonBlockingStep}
            action={discardAction}
        />
    );
};

export const backgroundColorForStatus = (releaseStepStatus: ReleaseStepStatus) => {
    switch (releaseStepStatus) {
        case ReleaseStepStatus.Ready:
            return colorWithAlpha(rwColors.statusGreen, 0.1);
        case ReleaseStepStatus.Pending:
        case ReleaseStepStatus.PendingChecklistOnly:
            return antGold.gold1;
        case ReleaseStepStatus.Blocked:
            return antRed.red1;
        case ReleaseStepStatus.Inactive:
        case ReleaseStepStatus.Skipped:
            return antGrey.grey2;
    }
};

export const outlineColorForStatus = (releaseStepStatus: ReleaseStepStatus) => {
    switch (releaseStepStatus) {
        case ReleaseStepStatus.Ready:
            return colorWithAlpha(rwColors.statusGreen, 0.5);
        case ReleaseStepStatus.Pending:
        case ReleaseStepStatus.PendingChecklistOnly:
            return antGold.gold4;
        case ReleaseStepStatus.Blocked:
            return antRed.red4;
        case ReleaseStepStatus.Inactive:
        case ReleaseStepStatus.Skipped:
            return antGrey.grey5;
    }
};

const alertTypeForStatus: (releaseStepStatus: ReleaseStepStatus) => 'success' | 'warning' | 'error' | 'info' = (
    releaseStepStatus,
) => {
    switch (releaseStepStatus) {
        case ReleaseStepStatus.Ready:
            return 'success';
        case ReleaseStepStatus.Pending:
        case ReleaseStepStatus.PendingChecklistOnly:
            return 'warning';
        case ReleaseStepStatus.Blocked:
            return 'error';
        case ReleaseStepStatus.Inactive:
        case ReleaseStepStatus.Skipped:
            return 'info';
    }
};

export const iconForStatus = (releaseStepStatus: ReleaseStepStatus, nonBlockingStep: boolean, isDarkMode = false) => {
    const iconColor = nonBlockingStep ? antGrey.grey6 : null;
    const twoToneColor = (primary: string): string | [string, string] =>
        isDarkMode ? [primary, 'transparent'] : primary;

    switch (releaseStepStatus) {
        case ReleaseStepStatus.Ready:
            return <CheckCircleFilled style={{ color: iconColor ?? runwayColors.statusGreen }} />;
        case ReleaseStepStatus.Pending:
        case ReleaseStepStatus.PendingChecklistOnly:
            return <ClockCircleTwoTone twoToneColor={twoToneColor(iconColor ?? runwayColors.statusYellow)} />;
        case ReleaseStepStatus.Blocked:
            return <CloseCircleTwoTone twoToneColor={twoToneColor(iconColor ?? runwayColors.statusRed)} />;
        case ReleaseStepStatus.Inactive:
        case ReleaseStepStatus.Skipped:
            return <ClockCircleOutlined style={{ color: iconColor ?? runwayColors.statusGrey }} />;
    }
};

export default StepStatusHeaderComponent;
